import { generatePatternStyles, hasGradient } from '@web-stories-wp/patterns';
import type { Pattern } from '@web-stories-wp/patterns';
import {
  GRADIENT_PATTERN_STYLE_PROP,
  encodeGradientPattern,
  decodeGradientPattern,
  getGradientFallbackColor,
  getGradientPatternStyleSelector,
  getGradientPatternStyleValue,
  hasGradientPatternString,
  removeGradientPatternStyleProps,
} from '../../utils/textGradient';
import { sanitizeRichText } from '../../utils/sanitizeMarkup';
import { CSS_VARIABLE_PREFIX } from './constants';

function applyGradientInlineStyle(element: HTMLElement, pattern: Pattern) {
  removeGradientPatternStyleProps(element.style);
  const encoded = encodeGradientPattern(pattern);
  element.style.setProperty(GRADIENT_PATTERN_STYLE_PROP, encoded);
  const { backgroundImage } = generatePatternStyles(
    { ...pattern, geometryVersion: undefined },
    'background'
  );
  if (backgroundImage) {
    element.style.backgroundImage = backgroundImage;
  }
  const fallbackColor = getGradientFallbackColor(pattern);
  if (fallbackColor) {
    element.style.color = fallbackColor;
  }
  element.style.webkitBackgroundClip = 'text';
  element.style.backgroundClip = 'text';
  element.style.webkitTextFillColor = 'transparent';
}

function applySolidInlineStyle(element: HTMLElement, pattern: Pattern) {
  removeGradientPatternStyleProps(element.style);
  element.style.backgroundImage = '';
  element.style.backgroundClip = '';
  element.style.webkitBackgroundClip = '';
  element.style.webkitTextFillColor = '';
  const { color } = generatePatternStyles(pattern, 'color', pattern.id);
  if (color) {
    element.style.color = color;
  }
}

export function getIdAndColorFromStringWithVariable(str: string) {
  // eg var(--custom-color-04957ee6-3a9d-4a90-9b40-816724c8d232, #b6b6b6)
  const pattern = `var\\(${CSS_VARIABLE_PREFIX}([a-zA-Z0-9-]+), (.+)\\)`;
  const regex = new RegExp(pattern);
  const match = str.match(regex);

  if (match) {
    const colorId = match[1];
    const color = match[2];
    return { color, colorId };
  }

  return { color: str, colorId: null };
}

export function replaceVariableWithColor({
  colorId,
  detachColor,
  hexValue,
  originalString,
  useVariable,
}: {
  colorId: string;
  detachColor: boolean;
  hexValue: string;
  originalString: string;
  useVariable: boolean;
}) {
  let newStr = originalString;
  const pattern = `var\\(${CSS_VARIABLE_PREFIX}${colorId}, ([^)\\r\\n]+)\\)*`;
  const regex = new RegExp(pattern, 'g');
  const matches = Array.from(originalString.matchAll(regex));

  for (const [variable, previousHexValue] of matches) {
    if (detachColor) {
      // Detach the variable from the color, but don't update the colour itself
      newStr = newStr.replace(variable, previousHexValue);
    } else {
      // Keep the variable (so that we know this element uses the saved color)
      // but update the fallback for the Studio Page Generator that doesn't have access to the saved color
      newStr = newStr.replace(
        variable,
        useVariable
          ? `var(${CSS_VARIABLE_PREFIX}${colorId}, ${hexValue})`
          : hexValue
      );
    }
  }

  return newStr;
}

export function replaceInlineGradientWithColor({
  colorId,
  editedColor,
  originalString,
  action,
}: {
  colorId: string;
  editedColor: Pattern;
  originalString: string;
  action: 'addOrEdit' | 'delete';
}) {
  if (
    !originalString ||
    typeof document === 'undefined' ||
    !hasGradientPatternString(originalString)
  ) {
    return originalString;
  }

  const container = document.createElement('div');
  container.innerHTML = sanitizeRichText(originalString);

  const elements = container.querySelectorAll<HTMLElement>(
    getGradientPatternStyleSelector()
  );

  let hasChanges = false;

  elements.forEach((element) => {
    const encoded = getGradientPatternStyleValue(element.style).trim();
    if (!encoded) {
      return;
    }
    const pattern = decodeGradientPattern(encoded);
    if (!pattern || pattern.id !== colorId) {
      return;
    }

    hasChanges = true;

    if (action === 'delete') {
      const detachedPattern = { ...pattern };
      delete detachedPattern.id;
      applyGradientInlineStyle(element, detachedPattern);
      return;
    }

    if (hasGradient(editedColor)) {
      applyGradientInlineStyle(element, editedColor);
      return;
    }

    applySolidInlineStyle(element, editedColor);
  });

  return hasChanges ? container.innerHTML : originalString;
}
